/**
 * Shooting System & Raycast Hit Detection for Aim Assist Training Lab
 * Precision center raycasting with bullet bloom, in-air jump spread, and 3D tracers
 */

import * as THREE from 'three';
import { GAME_PRESETS } from './constants.js';

export class ShootingSystem {
  constructor(camera, targetManager, weaponController, audioManager, settingsStore, sceneManager) {
    this.camera = camera;
    this.targetManager = targetManager;
    this.weaponController = weaponController;
    this.audioManager = audioManager;
    this.settingsStore = settingsStore;
    this.sceneManager = sceneManager;

    this.raycaster = new THREE.Raycaster();
    this.centerCoord = new THREE.Vector2(0, 0); // Exact screen center crosshair
    this.spreadCoord = new THREE.Vector2(0, 0);
  }

  /**
   * Evaluates if crosshair is currently hovering over an active target.
   * Crucial for continuous tracking in Tracking Mode.
   */
  evaluateTrackingIntersection() {
    this.raycaster.setFromCamera(this.centerCoord, this.camera);
    const activeTargets = this.targetManager.activeTargets;
    if (activeTargets.length === 0) return null;

    const colliders = activeTargets.map((t) => t.collider || t.mesh);
    const intersects = this.raycaster.intersectObjects(colliders, true);

    if (intersects.length > 0) {
      const hitObj = intersects[0].object;
      const targetObj = activeTargets.find(
        (t) => t.collider === hitObj || t.mesh === hitObj || t.mesh.children.includes(hitObj)
      );
      if (targetObj) {
        return {
          target: targetObj,
          point: intersects[0].point,
          distance: intersects[0].distance
        };
      }
    }
    return null;
  }

  /**
   * Fires a shot from the camera.
   * Takes movement speed, counter-strafe, airborne state, and weapon spray pattern offset into account.
   */
  processFire(currentTime, movementSpeed = 0, isCounterStrafing = true, isAirborne = false, sprayOffset = null) {
    // 1. Calculate spread based on first-shot accuracy, movement penalty, and airborne jump state
    const gamePreset = GAME_PRESETS[this.settingsStore.get('selectedGame')] || GAME_PRESETS.valorant;
    let baseSpread = this.weaponController.weaponConfig?.firstShotSpread || 0.0012;
    let movementBloom = 0;

    // Movement Error: Shooting while running or walking causes severe inaccuracy
    if (movementSpeed > 0.30 && !isCounterStrafing) {
      const penalty = gamePreset.movementPenalty || 0.85;
      movementBloom = penalty * 0.052 * Math.min(movementSpeed / 4.2, 1.4);
    }

    if (isAirborne) {
      movementBloom += 0.088; // Extreme jump inaccuracy penalty
    }

    const totalSpread = baseSpread + movementBloom;
    const fired = this.weaponController.fire(currentTime, totalSpread);
    if (!fired) return null;

    // 2. Compute raycast direction combining systematic weapon spray offset and bloom
    const sprayX = sprayOffset ? sprayOffset.x : 0;
    const sprayY = sprayOffset ? sprayOffset.y : 0;

    if (totalSpread > 0.001 || sprayX !== 0 || sprayY !== 0) {
      this.spreadCoord.set(
        sprayX + (Math.random() - 0.5) * totalSpread,
        sprayY + (Math.random() - 0.5) * totalSpread
      );
    } else {
      this.spreadCoord.set(0, 0);
    }

    this.raycaster.setFromCamera(this.spreadCoord, this.camera);
    const activeTargets = this.targetManager.activeTargets;
    const colliders = activeTargets.map((t) => t.collider || t.mesh);
    const intersects = this.raycaster.intersectObjects(colliders, true);

    const muzzlePos = this.weaponController.getMuzzleWorldPosition();
    const tracerColor = this.weaponController.weaponConfig?.tracerColor || 0x38bdf8;

    if (intersects.length > 0) {
      const firstIntersect = intersects[0];
      const hitMesh = firstIntersect.object;
      const targetObj = activeTargets.find(
        (t) => t.collider === hitMesh || t.mesh === hitMesh || t.mesh.children.includes(hitMesh)
      );

      if (targetObj) {
        // Test if hit is close to core (headshot/bullseye)
        const corePos = new THREE.Vector3();
        targetObj.coreMesh.getWorldPosition(corePos);
        const distFromCenter = firstIntersect.point.distanceTo(corePos);
        const isBullseye = distFromCenter <= (targetObj.targetScale * 0.22);

        // 3D Bullet Tracer & Spark Blast
        this.sceneManager?.addBulletTracer(muzzlePos, firstIntersect.point, tracerColor);
        this.sceneManager?.spawnHitSparks(firstIntersect.point, isBullseye ? 0xfacc15 : 0x38bdf8, 14);

        this.audioManager.playHit(isBullseye);
        return {
          hit: true,
          target: targetObj,
          point: firstIntersect.point,
          distance: firstIntersect.distance,
          isBullseye
        };
      }
    }

    // Shot missed: Tracer streaks into the distance
    const missPoint = new THREE.Vector3();
    this.raycaster.ray.at(42, missPoint);
    this.sceneManager?.addBulletTracer(muzzlePos, missPoint, tracerColor);

    return { hit: false };
  }
}
